import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import classNames from "classnames";
import { Helmet } from "react-helmet-async";
import { isEmpty, get } from "lodash";
import { readString } from "react-papaparse";
import { actions } from "../../components/organisms/Drawer/drawers.reducers";
import {
  updateRecordIntoTable,
  selectSelectedDataTable,
  fetchAllRecordsFromTable,
  selectTableData,
  selectCollections,
  selectSelectedDataModelId,
  addNewRecordIntoTable,
  selectSelectedDataModel,
  selectFormattedFilters,
  selectTableDataFilters,
  fetchReferenceCollectionsData,
  selectDataModelRegions,
  selectSelectedRecords,
  importCSV,
} from "./index";
import {
  DRAWERS,
  INTERFACES,
  OPERATIONS,
} from "../../components/organisms/AppDrawers/drawers.constants";
import { MODALS } from "../../components/organisms/AppModals/modals.constants";
import {
  API_RESPONSES,
  EMPTY_STRING,
  INDEX,
  REGEX,
  STATUS,
  TABS,
  EMPTY_ARRAY,
  DEFAULT_LIMIT,
  ZERO,
  ALERT_MESSAGES,
  REQUEST_STATUS,
} from "../../constants";
import {
  SuggestionBar,
  CustomTableHeader,
  Pagination,
  DataLimitFilter,
} from "../../components/molecules";
import { actions as dataModelActions } from "./dataModels.reducers";
import { actions as modalActions } from "../../components/organisms/Modal/modal.reducers";
import { selectSelectedModal } from "../../components/organisms/Modal";
import DeleteConfirmationModal from "../../components/organisms/AppModals/DeleteConfirmationModal";
import JsonModal from "../../components/organisms/AppModals/JsonModel";
import {
  dbSidePanelIcon,
  chevronLeftWhiteIcon,
  deleteIcon,
  menuEditIcon,
  filterIcon,
  refreshIndigoIcon,
  plusLightIcon,
} from "../../assets/icons";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { actions as breadCrumbActions } from "../../components/organisms/BreadCrumb/breadCrumb.reducers";
import { getCellRenderer, getEditor, renderNoDataModelLayout } from "../../helpers";
import Alert, { selectAlert } from "../../components/organisms/Alert";
import { getDataModelSuggestionMessage, getRegion } from "./services.helpers";
import Spinner, { SPINNER_NAMES } from "../../components/organisms/Spinner";

const { setSelectedDrawer } = actions;
const {
  setSelectedDataModelId,
  setSelectedCollectionId,
  setSelectedDataTable,
  setTableDataFilters,
  setFormattedFilters,
  resetTableData,
  resetReferenceCollectionsData,
  setSelectedRecords,
  updateTableData,
} = dataModelActions;
const { setSelectedModal } = modalActions;
const { setSelectedTab } = breadCrumbActions;

const { MANAGE_RECORD_DRAWER } = DRAWERS;
const { DELETE_MODAL, FILTER_DATA_MODAL} = MODALS;
const STRING = "string";
const BASE_64_URL = "base64Url";
const { FIRST } = INDEX;
const { FILE, IMAGE, EMAIL, BOOLEAN, JSON_TYPE } = INTERFACES;
const { INACTIVE } = STATUS;
const { SETTINGS } = TABS;
const { SUCCESS } = API_RESPONSES;
const {
  UPDATE_RECORD,
  ADD_RECORD,
  FETCH_ALL_RECORDS_FROM_TABLE,
  IMPORT_CSV,
} = SPINNER_NAMES;
const RECORDS_PER_PAGE = 12;
const TIME_OUT = 10;
const ONE = 1;
const FIVE = 5;
const HTTPS = "https";
const referencePropertiesDescription = ["Dropdown", "One-to-Many"];
const NO_ROWS_TO_SHOW = "No rows to show";
const {
  REQUIRED_FIELDS_ARE_EMPTY,
  EMAIL_IS_NOT_VALID,
  DATA_SAVED_SUCCESSFULLY,
} = ALERT_MESSAGES;
const { REJECTED } = REQUEST_STATUS;

const CustomOverlay = ({ spinnerName }) => {
  const [isRecordFetching, setIsRecordFetching] = useState(true);

  return (
    <Spinner name={spinnerName} setIsSpinnerActive={setIsRecordFetching}>
      {!isRecordFetching && NO_ROWS_TO_SHOW}
    </Spinner>
  );
};

const haveSameKeys = (keys1, keys2) =>
  keys1.length - ONE === keys2.length &&
  keys1.every((key) => key === "id" || keys2.includes(key));

const getColumnNames = (properties) =>
  properties.map((prop) => prop.schema.key);

const TableData = () => {
  const { dataModelId, tableId, tableRef } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const gridRef = useRef();
  const selectedDataModelId = useSelector(selectSelectedDataModelId);
  const selectedTable = useSelector(selectSelectedDataTable);
  const {
    data: selectedTableData,
    total: totalData,
    page: currentPage,
  } = useSelector(selectTableData);
  const selectedCollections = useSelector(selectCollections);
  const selectedDataModel = useSelector(selectSelectedDataModel);
  const selectedAlert = useSelector(selectAlert);
  const formattedFilters = useSelector(selectFormattedFilters);
  const tableDataFilters = useSelector(selectTableDataFilters);
  const dataModelRegions = useSelector(selectDataModelRegions);
  const selectedRecords = useSelector(selectSelectedRecords);
  const selectedModal = useSelector(selectSelectedModal);

  const [shouldShowSaveButton, setShouldShowSaveButton] = useState(false);
  const [selectedRows, setSelectedRows] = useState(EMPTY_ARRAY);
  const [shouldShowDeleteModal, setShouldShowDeleteModal] = useState(false);
  const [shouldShowJsonModal, setShouldShowJsonModal] = useState(false);
  const [newRowData, setNewRowData] = useState();
  const [primaryKey, setPrimaryKey] = useState();
  const [requiredFields, setRequiredFields] = useState(EMPTY_ARRAY);
  const [rawData, setRawData] = useState();
  const [visibleFields, setVisibleFields] = useState(EMPTY_ARRAY);
  const [emailFields, setEmailFields] = useState(EMPTY_ARRAY);
  const [fileTypeFields, setFileTypeFields] = useState(EMPTY_ARRAY);
  const [shouldShowSuggestionBar, setShouldShowSuggestionBar] = useState(false);
  const [alertData, setAlertData] = useState(selectedAlert);
  const [pageCount, setPageCount] = useState(ONE);
  const [newRowIndex, setNewRowIndex] = useState();
  const [colKey, setColKey] = useState();
  const [isNewRowAdded, setIsNewRowAdded] = useState(false);
  const [isSpinnerActive, setIsSpinnerActive] = useState(false);
  const [isNewRecordButton, setIsNewRecordButton] = useState(true);
  const [tableDataLimit, setTableDataLimit] = useState(DEFAULT_LIMIT);
  const [inputData, setInputData] = useState(EMPTY_STRING);
  const [gridData, setGridData] = useState(EMPTY_ARRAY);

  const resetFilters = useCallback(() => {
    dispatch(setTableDataFilters());
    dispatch(setFormattedFilters());
  }, [dispatch]);

  useEffect(() => () => dispatch(resetTableData()), [dispatch]);

  useEffect(() => {
    dispatch(
      fetchAllRecordsFromTable({
        id: dataModelId,
        tableName: tableRef,
      })
    );
  }, [dispatch, dataModelId, tableRef]);

  useEffect(() => {
    setSelectedRows(selectedRecords);
  }, [selectedRecords]);

  useEffect(() => {
    isEmpty(selectedRows) && gridRef.current?.api?.deselectAll();
  }, [selectedRows]);

  useEffect(() => {
    setAlertData(selectedAlert);
  }, [selectedAlert]);

  useEffect(() => {
    if (selectedTable) {
      const primaryKeyField = selectedTable.schema.properties.find(
        (each) => each.meta.isKey
      );

      const rowWithDefaultValues = selectedTable.schema.properties
        .filter((each) => !each.meta.isKey)
        .map((item) => ({
          [item.schema.key]: item.schema.defaultValue,
        }));
      const defaultData = Object.assign({}, ...rowWithDefaultValues);
      setRawData(defaultData);
      primaryKeyField && setPrimaryKey(primaryKeyField.schema.key);
    }
  }, [selectedTable]);

  useEffect(() => {
    if (tableId && !selectedTable) {
      dispatch(
        setSelectedDataTable(
          ...selectedCollections.data.filter((row) => row.id === tableId)
        )
      );
    }
  }, [dispatch, dataModelId, tableId, selectedCollections, selectedTable]);

  useEffect(() => {
    if (dataModelId && tableId) {
      dispatch(setSelectedCollectionId(tableId));
      dispatch(setSelectedDataModelId(dataModelId));
    }
  }, [dispatch, dataModelId, tableId]);

  useEffect(() => {
    if (selectedTableData && selectedTableData.length) {
      setShouldShowSaveButton(false);
      const shallowCloneGridData = selectedTableData.map((row) => ({ ...row }));
      setGridData(shallowCloneGridData);
    } else setGridData(EMPTY_ARRAY)
  }, [selectedTableData]);

  useEffect(() => {
    totalData && setPageCount(Math.ceil(totalData / tableDataLimit));
  }, [totalData, tableDataLimit]);

  useEffect(() => () => dispatch(resetReferenceCollectionsData()), [dispatch]);

  useEffect(() => {
    const selectedTableProperties = get(
      selectedTable,
      ["schema", "properties"],
      EMPTY_ARRAY
    );
    const referenceTypeProperties = selectedTableProperties.filter((property) =>
      referencePropertiesDescription.includes(property.meta.description)
    );
    referenceTypeProperties.forEach(({ schema }) => {
      const referenceCollection = selectedCollections.data.find(
        (collection) => collection.id === schema.referenceTableId
      );
      referenceCollection &&
        dispatch(
          fetchReferenceCollectionsData({
            id: dataModelId,
            tableName: referenceCollection.refName,
          })
        );
    });
  }, [selectedTable, dispatch, dataModelId, selectedCollections]);

  useEffect(() => {
    if (selectedTable) {
      const visibleFields = selectedTable.schema.properties.filter(
        (each) => !each.meta.hidden
      );
      const keysOfVisibleFields = visibleFields.map((each) => each.schema.key);
      setVisibleFields(keysOfVisibleFields);

      const requiredFields = visibleFields
        .filter(
          (property) =>
            property.meta.isRequired && property.meta.interface !== BOOLEAN
        )
        .map((each) => each.schema.key);
      setRequiredFields(requiredFields);

      const emailTypeFields = visibleFields
        .filter((each) => each.meta.interface === EMAIL)
        .map((each) => each.schema.key);
      setEmailFields(emailTypeFields);

      const fileTypeFields = visibleFields
        .filter(
          (each) =>
            each.meta.interface === FILE || each.meta.interface === IMAGE
        )
        .map((each) => each.schema.key);
      setFileTypeFields(fileTypeFields);
    }
  }, [selectedTable]);

  useEffect(() => {
    if (selectedDataModel) {
      dispatch(setSelectedTab(selectedDataModel));
      const showSuggestionBar = Boolean(
        selectedDataModel.status === INACTIVE ||
        (dataModelRegions &&
          selectedDataModel.region !== getRegion(dataModelRegions))
      );
      setShouldShowSuggestionBar(showSuggestionBar);
    }
  }, [dispatch, selectedDataModel, dataModelRegions]);

  useEffect(() => {
    return () => {
      resetFilters();
    };
  }, [dispatch, resetFilters]);

  const handleOnGridReady = useCallback(() => {
    gridRef.current.api.sizeColumnsToFit({
      defaultMinWidth: 200,
    });
  }, []);

  useEffect(() => {
    if (gridRef.current?.api) handleOnGridReady();
  });

  useEffect(() => {
    if (isNewRowAdded) {
      setIsNewRowAdded(false);
      setTimeout(() => {
        gridRef.current.api?.startEditingCell({
          rowIndex: newRowIndex,
          colKey,
        });
      }, TIME_OUT);
    }
  }, [newRowIndex, colKey, isNewRowAdded]);

  const hasProperties =
    get(selectedTable, ["schema", "properties"], EMPTY_ARRAY).length > ONE;

  const checkIsButtonDisabled = () => shouldShowSaveButton || !hasProperties;
  const checkIsValidEmail = (email) => !email || REGEX.EMAIL.test(email);

  const renderHeader = (props) => (
    <CustomTableHeader shouldShowSaveButton={shouldShowSaveButton} {...props} />
  );

  const getFormattedColumns = () => {
    if (selectedTable?.schema) {
      const { properties } = selectedTable.schema;
      return properties.map((each) => {
        const cellRendererFramework = getCellRenderer(each);
        const cellEditor = getEditor(each);
        const editable = !each.meta.readOnly;
        return {
          headerName: each.name,
          field: each.schema.key,
          resizable: true,
          headerComponent: renderHeader,
          cellRenderer: cellRendererFramework,
          cellEditor,
          hide: each.meta.hidden,
          editable,
        };
      });
    }

    return EMPTY_ARRAY;
  };

  const handleRowSelection = useCallback(() => {
    setSelectedRows(gridRef.current.api.getSelectedRows());
  }, []);

  const handleImportCSV =   async (event) => {
    const fileInput = event.target;
    const file = fileInput.files[ZERO];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (event) => {
      const csvData = event.target.result;
      const properties = selectedTable.schema.properties;
      const columns = getColumnNames(properties);

      try {
        const parsedData = await new Promise((resolve, reject) => {
          readString(csvData, {
            header: true,
            dynamicTyping: true,
            complete: (parsedData) => resolve(parsedData),
            error: (error) => reject(error),
          });
        });
        const inputFields = Object.keys(parsedData.data[ZERO]);

        if (haveSameKeys(columns, inputFields)) {
          await dispatch(
            importCSV({
              dataModelId,
              updatedTableDetails: {
                tableName: selectedTable.name,
                inputJson: parsedData.data,
              },
            })
          ).then(() => {
            setAlertData({
              type: SUCCESS,
              message: "Data imported Successfully",
            });
          });

          await dispatch(
            fetchAllRecordsFromTable({
              id: dataModelId,
              tableName: selectedTable.name,
            })
          );
        } else
          setAlertData({
            message: "Data not compatible for the particular table",
          });
        fileInput.value = null;
      } catch (error) {
        setAlertData({ message: "Unable to import CSV file" });
        fileInput.value = null;
      }
    };
    reader.readAsText(file);
  }

  const handleBulkEdit = useCallback(() => {
    dispatch(setSelectedDrawer(MANAGE_RECORD_DRAWER));
    dispatch(setSelectedRecords(selectedRows));
  }, [dispatch, selectedRows]);

  const handleSaveData = useCallback(
    (data) => {
      const page =
        !totalData || totalData % RECORDS_PER_PAGE
          ? pageCount
          : pageCount + ONE;
      fileTypeFields.forEach((key) => {
        if (
          data[key] &&
          typeof data[key] === STRING &&
          !data[key].startsWith(HTTPS)
        )
          data[key] = JSON.parse(data[key]);
      });

      const areFieldsChanged = !visibleFields.every((key) => !data[key]);
      const areRequiredFieldsEmpty = requiredFields.some((key) => !data[key]);
      const isValidEmail = !emailFields.some(
        (key) => !checkIsValidEmail(data[key])
      );

      const shouldSaveData =
        areFieldsChanged && !areRequiredFieldsEmpty && isValidEmail;

      areRequiredFieldsEmpty &&
        setAlertData({ message: REQUIRED_FIELDS_ARE_EMPTY });

      !areRequiredFieldsEmpty &&
        !isValidEmail &&
        setAlertData({
          message: EMAIL_IS_NOT_VALID,
        });

      if (shouldSaveData) {
        const rowData = {
          tableName: selectedTable.refName,
          schema: data,
        };
        dispatch(
          addNewRecordIntoTable({
            id: selectedDataModelId,
            rowData,
          })
        ).then((res) => {
          (+currentPage !== +page || res.meta.requestStatus === REJECTED) &&
            dispatch(
              fetchAllRecordsFromTable({
                id: selectedDataModelId,
                tableName: selectedTable.refName,
                filters: formattedFilters,
                page,
                limit: tableDataLimit,
              })
            );
          if (res?.payload?.id)
            dispatch(updateTableData({ ...data, id: res.payload.id }));
          if (res.payload)
            setAlertData({
              type: SUCCESS,
              message: DATA_SAVED_SUCCESSFULLY,
            });
          setShouldShowSaveButton(false);
          setSelectedRows(EMPTY_ARRAY);
          setNewRowData();
        });
      }
    },
    [
      dispatch,
      selectedDataModelId,
      selectedTable,
      visibleFields,
      requiredFields,
      emailFields,
      fileTypeFields,
      formattedFilters,
      pageCount,
      totalData,
      tableDataLimit,
      currentPage
    ]
  );

  const insertNewRow = useCallback(() => {
    const totalRowsCount = gridRef.current.api.getDisplayedRowCount();
    setTimeout(() => {
      const newRow = gridRef.current.api.applyTransaction({
        add: [{ ...rawData }],
      });

      setShouldShowSaveButton(true);
      gridRef.current.api.paginationGoToPage(
        totalRowsCount / RECORDS_PER_PAGE + ONE
      );

      setNewRowIndex(totalRowsCount);
      const prevSelectedRows = gridRef.current.api.getSelectedNodes();
      if (prevSelectedRows)
        prevSelectedRows.forEach((node) => node.setSelected(false));

      const firstColumn =
        gridRef.current.columnApi.getAllDisplayedColumns()[FIRST];
      setColKey(firstColumn.colId);

      const rowNode = gridRef.current.api.getRowNode(newRow.add[FIRST].id);

      rowNode.setSelected(true);
      setIsNewRowAdded(true);
      setNewRowData(rawData);
    }, TIME_OUT);
  }, [rawData]);

  const updateCellValue = useCallback(
    (record) => {
      if (!Object.prototype.hasOwnProperty.call(record.data, primaryKey))
        setNewRowData({ ...record.data });
    },
    [primaryKey]
  );

  const updateCellValueAndMakeApiCall = (params) => {
    const selectedIds = [params.data[primaryKey]];
    const updatedField = params.colDef.field;
    let updatedFieldValue = params.data[updatedField];

    const isRequiredField = requiredFields.includes(updatedField);
    if (isRequiredField && updatedFieldValue === EMPTY_STRING) return;

    const isEmailTypeField = emailFields.includes(updatedField);
    if (isEmailTypeField && !checkIsValidEmail(updatedFieldValue)) return;

    const isFileTypeField = fileTypeFields.includes(updatedField);
    if (
      isFileTypeField &&
      typeof updatedFieldValue === STRING &&
      updatedFieldValue.includes(BASE_64_URL)
    )
      updatedFieldValue = JSON.parse(updatedFieldValue);

    let updatedData = {
      selectedIds: selectedIds,
      tableName: selectedTable.refName,
      operation: OPERATIONS.UPDATE,
      tableFields: { [updatedField]: updatedFieldValue },
    };

    dispatch(
      updateRecordIntoTable({
        dataModelId,
        tableDataDetails: updatedData,
      })
    ).then(() => {
      dispatch(
        fetchAllRecordsFromTable({
          id: dataModelId,
          tableName: selectedTable.refName,
          filters: formattedFilters,
          limit: tableDataLimit,
        })
      );
    });
    setSelectedRows(EMPTY_ARRAY);
    setNewRowData();
    setShouldShowSaveButton(false);
  };

  const handleDeleteTableData = useCallback(() => {
    const updatedSelectedRows = gridRef.current.api.getSelectedRows();
    gridRef.current.api.applyTransaction({ remove: updatedSelectedRows });

    dispatch(
      updateRecordIntoTable({
        dataModelId: selectedDataModelId,
        tableDataDetails: {
          selectedIds: selectedRows.map((each) => each[primaryKey]),
          tableName: selectedTable.refName,
          operation: OPERATIONS.DELETE,
        },
      })
    ).then((res) => {
      if (res.payload) setShouldShowDeleteModal(false);
      dispatch(
        fetchAllRecordsFromTable({
          id: selectedDataModelId,
          tableName: selectedTable.refName,
          filters: formattedFilters,
          limit: tableDataLimit,
        })
      );
      dispatch(setSelectedModal(null));
    });
  }, [
    selectedRows,
    dispatch,
    selectedTable,
    selectedDataModelId,
    primaryKey,
    formattedFilters,
    tableDataLimit,
  ]);

  const handleOnDeleteButtonClicked = () => {
    setTimeout(() => {
      selectedRows.forEach((row) =>
        Object.hasOwn(row, primaryKey)
          ? setShouldShowDeleteModal(true)
          : (gridRef.current.api.applyTransaction({
              remove: selectedRows,
            }),
            setSelectedRows())
      );
      setNewRowData();
      setShouldShowSaveButton(false);
      dispatch(setSelectedModal(DELETE_MODAL));
    }, TIME_OUT);
    setIsNewRecordButton(true);
  };

  const handlePagination = useCallback(
    (value) => {
      dispatch(resetTableData());
      dispatch(
        fetchAllRecordsFromTable({
          id: selectedDataModelId,
          tableName: selectedTable.refName,
          filters: formattedFilters,
          page: value + ONE,
          limit: tableDataLimit,
        })
      );
    },
    [
      dispatch,
      formattedFilters,
      selectedDataModelId,
      selectedTable,
      tableDataLimit,
    ]
  );
  const onCellClicked = () => {
    const focusedCell = gridRef.current.api.getFocusedCell();
    const row = gridRef.current.api.getDisplayedRowAtIndex(focusedCell.rowIndex);
    const cellValue = gridRef.current.api.getValue(focusedCell.column, row);
    const property = selectedTable.schema.properties.find(
      (each) => each.name === focusedCell.column.getColDef().headerName
    );
    if (property.schema.type === JSON_TYPE) {
      setInputData(JSON.stringify(cellValue));
      setShouldShowJsonModal(true);
    }
  };
  const handleOnSubmit = useCallback(() => {
    const focusedCell = gridRef.current.api.getFocusedCell();
    const selectedNodes = gridRef.current.api.getSelectedNodes();
    const selectedRowId = selectedNodes[0].id;

    const rowNode = gridRef.current.api.getRowNode(selectedRowId);
    const columnId = focusedCell.column;
    const newValue = inputData;
    rowNode.setDataValue(columnId, newValue);
    gridRef.current.api.refreshCells({ rowNodes: [rowNode], columns: [columnId] });
    dispatch(setSelectedModal(null));
    setShouldShowJsonModal(false);
  }, [dispatch, inputData]);

  const handleOnLimitUpdate = (e) => {
    const updatedLimit = e.target.value;
    dispatch(resetTableData());
    dispatch(
      fetchAllRecordsFromTable({
        id: selectedDataModelId,
        tableName: selectedTable.refName,
        filters: formattedFilters,
        limit: updatedLimit,
      })
    );
    setTableDataLimit(updatedLimit);
  };

  return (
    <div className="content scrollbar bg-gray-900 overflow-auto p-6 flex-1 flex flex-col">
      <Helmet>
        <title>Table Data | TavaJS</title>
      </Helmet>
      <div className="flex-col xs:flex-row flex mb-4 gap-4">
        <button
          className="flex flex-row text-white items-center"
          onClick={() => navigate(-1)}
        >
          <img src={chevronLeftWhiteIcon} alt="navigate-back" />
          <span className="ml-1 font-semibold text-sm">
            {selectedTable?.name}
          </span>
        </button>
      </div>
      <div className="flex flex-col flex-1 grow">
        <Spinner name={FETCH_ALL_RECORDS_FROM_TABLE}>
        {selectedDataModel ? (
          <>
          {shouldShowSuggestionBar && (
            <SuggestionBar
              text={getDataModelSuggestionMessage(
                selectedDataModel,
                dataModelRegions
              )}
              link={SETTINGS}
              handleOnClick={() =>
                navigate(`/data-models/${dataModelId}/settings`)
              }
              classes="text-red-600"
            />
          )}
          <div className="flex flex-wrap justify-end gap-4 mb-4">
            <button
              className="btn btn-primary-light flex items-center leading-none disabled:cursor-not-allowed"
              onClick={() => dispatch(setSelectedModal(FILTER_DATA_MODAL))}
              disabled={!hasProperties || !(!shouldShowSaveButton && rawData)}
            >
              <div className="icon mr-2">
                <img src={filterIcon} width="20" alt="plusLightIcon" />
              </div>
              <span className="text-indigo-700">Filter</span>
              <span className="relative w-6 h-4 text-center ml-2 border-solid rounded-2xl bg-indigo-200 text-indigo-800 my-auto">
                <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                  {tableDataFilters.length}
                </span>
              </span>
            </button>
            {!isEmpty(formattedFilters) && (
              <button
                className="btn btn-primary-light flex items-center leading-none"
                onClick={resetFilters}
              >
                <div className="icon mr-2">
                  <img src={refreshIndigoIcon} width="15" alt="plusLightIcon" />
                </div>
                <span className="text-indigo-700">Reset Filter</span>
              </button>
            )}
            {!shouldShowSaveButton && rawData && isNewRecordButton && (
              <button
                className={classNames(
                  "btn btn-primary flex items-center leading-none",
                  {
                    "cursor-not-allowed": !hasProperties,
                  }
                )}
                disabled={checkIsButtonDisabled()}
                onClick={() => {
                  insertNewRow();
                  dispatch(setTableDataFilters());
                  dispatch(setFormattedFilters());
                }}
              >
                <div className="icon mr-2">
                  <img src={plusLightIcon} width="20" alt="plus-light-icon" />
                </div>
                <span className="text-gray-50">New Record</span>
              </button>
            )}
            {!shouldShowSaveButton && rawData && (
              <div>
                <button
                  type="button"
                  className="btn btn-primary p-0"
                  disabled={isSpinnerActive}
                >
                  <Spinner
                    name={IMPORT_CSV}
                    setIsSpinnerActive={setIsSpinnerActive}
                    persistSize
                  >
                    <label htmlFor="inputCSVFile" className="py-2 px-4">
                      Import
                    </label>
                  </Spinner>
                </button>
                <input
                  type="file"
                  id="inputCSVFile"
                  accept=".csv"
                  className="hidden"
                  onChange={handleImportCSV}
                />
              </div>
            )}
            {newRowData && !visibleFields.every((key) => !newRowData[key]) && (
              <button
                className="btn btn-primary leading-none"
                onClick={() => handleSaveData(newRowData)}
                disabled={isSpinnerActive}
              >
                <Spinner
                  name={ADD_RECORD}
                  setIsSpinnerActive={setIsSpinnerActive}
                >
                  <div className="flex items-center gap-2">
                    <img
                      src={dbSidePanelIcon}
                      className="!brightness-0 !invert w-4"
                      alt="dbSidePanelIcon"
                    />
                    <span className="text-gray-50">Save Data</span>
                  </div>
                </Spinner>
              </button>
            )}
            {selectedRows && (
              <>
                <button
                  className={classNames(
                    "btn btn-primary flex items-center leading-none",
                    {
                      hidden: shouldShowSaveButton || selectedRows.length < 2,
                    }
                  )}
                  onClick={() => handleBulkEdit()}
                >
                  <div className="icon mr-2 !brightness-0 !invert">
                    <img src={menuEditIcon} width="20" alt="menu-editIcon" />
                  </div>
                  <span className="text-gray-50">Bulk edit</span>
                </button>
                <button
                  className={classNames(
                    "btn btn-primary flex items-center leading-none",
                    { hidden: !selectedRows.length }
                  )}
                  onClick={handleOnDeleteButtonClicked}
                >
                  <div className="icon mr-2">
                    <img src={deleteIcon} width="20" alt="delete-icon" />
                  </div>
                  <span className="text-gray-50">
                    {selectedRows.length > ONE
                      ? "Bulk Delete"
                      : "Delete Record"}
                  </span>
                </button>
              </>
            )}
          </div>
          {alertData && (
            <div className="m-auto block pt-2 pb-4">
              <Alert
                type={alertData.type}
                message={alertData.message}
                handleOnClose={setAlertData}
              />
            </div>
          )}

          <AgGridReact
            deltaRowDataMode
            ref={gridRef}
            className="ag-theme-alpine"
            animateRows="true"
            columnDefs={getFormattedColumns()}
            rowData={gridData}
            rowSelection="multiple"
            domLayout={"autoHeight"}
            onRowSelected={handleRowSelection}
            onGridSizeChanged={handleOnGridReady}
            scrollbarWidth={FIVE}
            onCellValueChanged={
              shouldShowSaveButton
                ? updateCellValue
                : updateCellValueAndMakeApiCall
            }
            onGridReady={handleOnGridReady}
            noRowsOverlayComponent={CustomOverlay}
            noRowsOverlayComponentParams={{
              spinnerName: FETCH_ALL_RECORDS_FROM_TABLE,
            }}
            suppressDragLeaveHidesColumns={true}
            onCellClicked={onCellClicked}
          />
          {shouldShowJsonModal && (
            <JsonModal
            inputData={inputData}
            selectedModal={selectedModal}
            setInputData={setInputData}
            setShouldShowJsonModal={setShouldShowJsonModal}
            setSelectedModal={setSelectedModal}
            handleOnSubmit={handleOnSubmit}
          />
         )}

          {shouldShowDeleteModal && (
            <DeleteConfirmationModal
              handleDelete={handleDeleteTableData}
              setShouldShowDeleteModal={setShouldShowDeleteModal}
              spinner={UPDATE_RECORD}
              title="Delete Record"
              shouldShowConfirmationTextbox={false}
            />
          )}
          <div className="mt-2 flex justify-between">
            <div>
              <Pagination
                handlePagination={handlePagination}
                pagesCount={pageCount}
                activePage={currentPage - ONE}
                count={totalData}
              />
            </div>
            <div>
              {totalData > DEFAULT_LIMIT && (
                <DataLimitFilter
                  tableDataLimit={tableDataLimit}
                  handleOnChange={handleOnLimitUpdate}
                />
              )}
            </div>
          </div>
          </>
        ) : renderNoDataModelLayout(navigate)
        }
        </Spinner>
      </div>
    </div>
  );
};
export default TableData;
